import { Address } from "viem";
import { IS_TESTNET } from "~/core/constants";

const prefix = IS_TESTNET ? 'testnet' : 'mainnet'

interface BlinkoDB {
  db: IDBDatabase;
}

//
const openDatabase = (): Promise<BlinkoDB> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(`${prefix}-blinkodb-v1`, 1);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve({ db: request.result });
    request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains('storage')) {
        db.createObjectStore('storage');
      }
      if (!db.objectStoreNames.contains('storageByAddress')) {
        const storageByAddressStore = db.createObjectStore('storageByAddress');
        storageByAddressStore.createIndex('address', 'address', { unique: false });
      }
    };
  });
};

const getStore = async (db: IDBDatabase, storeName: string, mode: IDBTransactionMode = 'readonly') => {
    return db.transaction(storeName, mode).objectStore(storeName);
}

export class AsyncStorage {
  private key: string;
  private dbPromise: Promise<BlinkoDB>;

  constructor(key: string) {
    this.key = key;
    this.dbPromise = openDatabase();
  }

  async get(): Promise<string | null> {
    const { db } = await this.dbPromise;
    const store = await getStore(db, 'storage');
    return new Promise((resolve, reject) => {
      const request = store.get(this.key);
      request.onsuccess = () => resolve(request.result ?? null);
      request.onerror = () => reject(request.error);
    });
  }

  async set(value: string): Promise<void> {
    const { db } = await this.dbPromise;
    const store = await getStore(db, 'storage', 'readwrite');
    return new Promise((resolve, reject) => {
      const request = store.put(value, this.key);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async remove(): Promise<void> {
    const { db } = await this.dbPromise;
    const store = await getStore(db, 'storage', 'readwrite');
    return new Promise((resolve, reject) => {
      const request = store.delete(this.key);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }
}

export class AsyncStorageByAddress {
  private key: string;
  private dbPromise: Promise<BlinkoDB>;

  constructor(key: string) {
    this.key = key;
    this.dbPromise = openDatabase();
  }

  async get(address: Address): Promise<any | null> {
    const { db } = await this.dbPromise;
    const store = await getStore(db, 'storageByAddress');
    return new Promise((resolve, reject) => {
      const request = store.get(`${this.key}-${address}`);
      request.onsuccess = () => resolve(request.result ?? null);
      request.onerror = () => reject(request.error);
    });
  }

  async set(address: Address, value: any): Promise<void> {
    const { db } = await this.dbPromise;
    const store = await getStore(db, 'storageByAddress', 'readwrite');
    return new Promise((resolve, reject) => {
      const request = store.put(value, `${this.key}-${address}`);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async remove(address: Address): Promise<void> {
    const { db } = await this.dbPromise;
    const store = await getStore(db, 'storageByAddress', 'readwrite');
    return new Promise((resolve, reject) => {
      const request = store.delete(`${this.key}-${address}`);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }
}
